import { PropsWithChildren, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "react-error-boundary";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import NoData from "@/components/common/NoData";
import PageWrapper from "@/components/PageWrapper";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler";
import { logError } from "@/components/error-hanlder/error-handler.lib";

import { motion } from "framer-motion";
import { ArrowDownUp, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils.ts";
import IconDonFilled from "@/assets/icons/don-filled.svg?react";
import HistoryCardLoading from "./components/cards/HistoryCard/HistoryCardLoading";
import HistoryCard from "./components/cards/HistoryCard/HistoryCard.tsx";
import { useHistory } from "@/pages/history/api";

import { formatNumber } from "@/shared/utils";
import HistoryModalSheet from "./HistoryModalSheet.tsx";
import InfiniteScrollList, {
  checkFetchMore,
} from "@/components/base/InfiniteScrollList.tsx";
import { useInfiniteQueryFlatted } from "@/shared/hooks/useInfiniteQueryFlatted.ts";
import { Transaction } from "./types.ts";

function useFormatTransactions(transactions: Transaction[]) {
  const { t } = useTranslation();
  const grouped = transactions.reduce((acc, transaction) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateObj = new Date(transaction.created_at);
    const localDate = new Date(
      dateObj.toLocaleString("en-US", { timeZone: timezone })
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let formattedDate = localDate.toLocaleDateString("en-CA");

    if (localDate.toDateString() === today.toDateString()) {
      formattedDate = t("today");
    } else if (localDate.toDateString() === yesterday.toDateString()) {
      formattedDate = t("yesterday");
    }

    if (!acc[formattedDate]) {
      acc[formattedDate] = {
        date: dayjs(localDate).format("DD.MM.YYYY, HH:mm"),
        timezone,
        list: [],
        total: 0,
      };
    }

    const amount =
      parseFloat(transaction.amount) * (transaction.type === 1 ? 1 : -1);
    acc[formattedDate].total += amount;
    acc[formattedDate].list.push(transaction);
    return acc;
  }, {} as Record<string, { date: string; timezone: string; list: Transaction[]; total: number }>);

  return Object.values(grouped);
}

type FilterType = null | 1 | -1;

export default function History() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterType>(null);

  const segments: {
    label: string;
    value: FilterType;
    Icon: typeof ArrowDownUp;
  }[] = [
    { label: t("all_button"), value: null, Icon: ArrowDownUp },
    { label: t("income"), value: 1, Icon: ArrowDownLeft },
    { label: t("outcome"), value: -1, Icon: ArrowUpRight },
  ];

  // Per-segment icon colour conveys the semantic: income → green,
  // outcome → red, all → neutral white. The pill background stays the
  // original neutral white/20.
  const iconColor = (value: FilterType) => {
    if (value === 1) return "text-green";
    if (value === -1) return "text-red";
    return "text-white";
  };

  return (
    <PageWrapper backPath="/">
      {/* Transaction filter — original neutral white/20 sliding pill with
          per-segment lucide icons coloured by semantic (income green,
          outcome red, all white). */}
      <div className="container py-3 pb-2">
        <div className="text-white bg-white/08 rounded-lg flex items-center justify-center relative p-1">
          {segments.map((seg) => (
            <VibratingButton
              key={String(seg.value)}
              onClick={() => setFilter(seg.value)}
              vibrationType="selection"
              className="relative flex items-center justify-center gap-1.5 text-sm font-medium py-1.5 leading-[18px] rounded-md transition-all duration-500 z-10 w-full"
            >
              {filter === seg.value && (
                <motion.div
                  layoutId="history-filter-pill"
                  className="absolute inset-0 bg-white/20 rounded-md -z-10"
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                />
              )}
              <seg.Icon
                className={cn("size-4 shrink-0", iconColor(seg.value))}
                strokeWidth={2.2}
              />
              <span className="relative">{seg.label}</span>
            </VibratingButton>
          ))}
        </div>
      </div>

      <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
        <Suspense
          fallback={
            <HistoryListWrapper>
              {Array.from({ length: 10 }).map((_, idx) => (
                <HistoryCardLoading key={idx} />
              ))}
            </HistoryListWrapper>
          }
        >
          <BaseHistoryAll filter={filter} />
        </Suspense>
      </ErrorBoundary>
    </PageWrapper>
  );
}

const HistoryListWrapper = (props: PropsWithChildren) => {
  return <div className="grid gap-2 container pb-4">{props.children}</div>;
};

function BaseHistoryAll({ filter }: { filter?: FilterType }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  const queryResult = useSuspenseInfiniteQuery(useHistory(filter));

  const historyData = useInfiniteQueryFlatted(queryResult.data);
  const historyDataFormatted = useFormatTransactions(historyData);

  const handleCardClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <>
      <HistoryListWrapper>
        <InfiniteScrollList
          isFetchingNextPage={queryResult.isFetchingNextPage}
          fetchNextPage={queryResult.fetchNextPage}
          canFetchMore={checkFetchMore(queryResult)}
          placeholder={<HistoryCardLoading />}
          className="gap-3"
        >
          {historyData.length === 0 ? (
            <div className="flex-1 flex-center min-h-[60vh]">
              <NoData
                title={t("history_not_found")}
                description={t("history_not_found_description")}
                image={<IconDonFilled width="48" height="48" />}
              />
            </div>
          ) : (
            historyDataFormatted.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between py-2 container">
                  <p className="text-xs text-gray-1 text-right font-medium leading-130">
                    {item.date}
                  </p>
                  <div className="flex gap-1 justify-end items-center">
                    <span className="text-gray-1 text-xs font-medium leading-130 flex-y-center gap-px">
                      {(item.total <= 0 ? "" : "+") + formatNumber(item.total)}{" "}
                      DON
                    </span>
                  </div>
                </div>
                <div className="grid gap-3">
                  {item.list.map((child) => (
                    <div
                      key={child.id}
                      onClick={() => handleCardClick(child)}
                      className="cursor-pointer"
                    >
                      <HistoryCard {...child} />
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </InfiniteScrollList>
      </HistoryListWrapper>
      <HistoryModalSheet
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        transaction={selectedTransaction}
      />
    </>
  );
}
